<template lang="pug">
  Row.col-layout(:align-items="alignItems" :gutters="gutters" :stack-gutters="stackGutters" :no-gutters="noGutters")
    Column(v-for="col in cols" :key="col.slot" :desktop="col.desktop" :tablet="col.tablet" :mobile="col.mobile")
      slot(:name="col.slot")
</template>
<script setup lang="ts">
import { computed } from 'vue'
import Row from './Row.vue'
import Column from './Column.vue'

type Gutters = 'none' | 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'

interface ColConfig {
  slot: string
  desktop: string
  tablet: string
  mobile: string
}

const props = withDefaults(defineProps<{
  columns?: number
  layout?: string
  /**
   * Removes gutters from row
   */
  noGutters?: boolean
  /**
   * Removes gutters from child Columns
   */
  gutters?: Gutters
  stackGutters?: Gutters
  stackTablet?: boolean
  stackMobile?: boolean
  /**
   * Align items within Row's flex container
   */
  alignItems?: string
}>(), {
  columns      : 2,
  layout       : '6/6',
  noGutters    : false,
  stackGutters : 'none',
  stackTablet  : true,
  stackMobile  : true,
  alignItems   : 'flex-start',
})

const cols = computed<ColConfig[]>(() => {
  if (props.columns === 2) {
    const [col1, col2] = props.layout.split('/')

    return [
      { slot: 'col1', desktop: col1, tablet: props.stackTablet ? '12' : col1, mobile: props.stackMobile ? '12' : col1 },
      { slot: 'col2', desktop: col2, tablet: props.stackTablet ? '12' : col2, mobile: props.stackMobile ? '12' : col2 },
    ]
  }

  const desktop = `${12 / props.columns}`
  const nonStacked = props.columns === 4 ? '6' : desktop

  const cols: ColConfig[] = []
  for (let num = 1; num <= props.columns; num += 1) {
    cols.push({ slot: `col${num}`, desktop, tablet: props.stackTablet ? '12' : nonStacked, mobile: props.stackMobile ? '12' : nonStacked })
  }

  return cols
})
</script>

<style lang="scss">
.product-pg .grid-tabs, .product-pg .grid-main {
  @include tablet-and-small-desktop() {
    .col-layout .column {
      width: 100%;
    }
  }
}

.drawer-body{
  .col-layout .row {
    flex-direction: column;
  }
}

.col-layout .row {

  @include all-desktop() {
    display: flex;
    flex-flow: row;
    flex-direction: row;
  }

  &.gutters-xxs {
    gap: 12px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }
  &.gutters-xs {
    gap: 16px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }
  &.gutters-s {
    gap: 20px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }
  &.gutters-m {
    gap: 24px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }
  &.gutters-l {
    gap: 32px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }
  &.gutters-xl {
    gap: 40px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }
  &.gutters-xxl {
    gap: 48px;
    .column {
      flex: 1;
      min-width: 0;
    }
  }

  @include not-desktop() {
    &.gutters-xxs, &.gutters-xs, &.gutters-s,
    &.gutters-m, &.gutters-l, &.gutters-xl, &.gutters-xxl {
      gap: unset;
      flex-wrap: wrap;

      .column {
        flex: unset;
        min-width: unset;
      }
    }

    // stack-gutters-* uses row-gap so it only ever adds space between
    // wrapped lines — it can't eat into column-gap/width when stackMobile is false
    &.stack-gutters-xxs { row-gap: 12px; .column { padding-bottom: 0; } }
    &.stack-gutters-xs  { row-gap: 16px; .column { padding-bottom: 0; } }
    &.stack-gutters-s   { row-gap: 20px; .column { padding-bottom: 0; } }
    &.stack-gutters-m   { row-gap: 24px; .column { padding-bottom: 0; } }
    &.stack-gutters-l   { row-gap: 32px; .column { padding-bottom: 0; } }
    &.stack-gutters-xl  { row-gap: 40px; .column { padding-bottom: 0; } }
    &.stack-gutters-xxl { row-gap: 48px; .column { padding-bottom: 0; } }
 }}

</style>
